import React, { useState } from 'react';
import { Popup } from 'semantic-ui-react';
import type { PopupProps } from 'semantic-ui-react/dist/commonjs/modules/Popup/Popup';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { IconButton } from '../Button/IconButton';

export type TooltipProps = {
  anchor?: React.ReactNode;
  children: React.ReactNode;
  /**
   * Fades as it opens. **There is no fade as it closes** - the popup unmounts rather than closing,
   * so by the time anything could animate it is already gone.
   */
  fade?: boolean;
  /**
   * Takes the close button away, leaving a tooltip that goes when the pointer does.
   *
   * **For a hint, not for a notice.** The button is there so a reader who opened the thing can put
   * it back - which matters for a surface holding something to read, and is furniture on one holding
   * half a sentence. It also earns its place on touch, where there is no pointer to leave.
   */
  hideClose?: boolean;
  name?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'top center';
  /**
   * `minimal` for a hint rather than a card: tighter padding, smaller text and a narrower cap. It
   * says nothing about the close button - that is `hideClose`.
   */
  variation?: 'normal' | 'minimal';
};

const positionMappings: Record<
  NonNullable<TooltipProps['position']>,
  PopupProps['position']
> = {
  top: 'top right',
  bottom: 'bottom left',
  left: 'left center',
  right: 'right center',
  'top center': 'top center',
};

export const Tooltip = ({
  anchor,
  children,
  fade,
  hideClose = false,
  name = 'tooltip',
  position = 'top',
  variation = 'normal',
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMinimal = variation === 'minimal';
  const positionMap = (positionMappings[position] ||
    position) as PopupProps['position'];

  return (
    <span data-test={`${name}-anchor`}>
      <Popup
        basic
        hideOnScroll
        /*
          The surface is portalled to `body`, so a class on it is the only way to reach it -
          `Tooltip.less` carries both of these.
        */
        className={AttrsHelper.formatClassname(
          isMinimal && 'tooltip-minimal',
          fade && 'tooltip-fade'
        )}
        content={
          <div className="tooltip-contents">
            <span data-test={`${name}-contents`}>{children}</span>
            {hideClose ? null : (
              <span data-test={`${name}-close`}>
                <IconButton
                  icon="x-mark"
                  name={`${name}-close-button`}
                  pattern="secondary"
                  variation="minimal"
                  onClick={() => {
                    setIsOpen(false);
                  }}
                />
              </span>
            )}
          </div>
        }
        open={isOpen}
        onOpen={() => {
          setIsOpen(true);
        }}
        onClose={() => {
          setIsOpen(false);
        }}
        position={positionMap}
        /*
          **The anchor is wrapped, not handed over bare.** Semantic's `Portal` makes a trigger
          interactive by cloning `onMouseEnter` and friends onto it, and a component that does not
          forward them drops them - so the trigger has to be a host element whatever the caller
          passes. `Tooltip.less` sizes it to its content, which is what Popper measures.
        */
        trigger={
          <span className="tooltip-trigger">
            {anchor ?? (
              <IconButton
                icon="circle-info"
                name={`${name}-button`}
                pattern="secondary"
                variation="minimal"
              />
            )}
          </span>
        }
      />
    </span>
  );
};
